import { useState } from 'react';
import { ChevronLeft, ChevronRight, Tags } from 'lucide-react';

import { useNamePatterns } from '@/api/name-pattern/use-name-patterns.ts';
import { NamePatternDtoTypeEnum } from '@/api/types';
import { Button } from '@/components/ui/button.tsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input.tsx';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs.tsx';
import NamePatternGroupRow from '@/features/name-patterns/name-pattern-group-row/name-pattern-group-row.tsx';
import AppContent from '@/layout/app-content';

const LIMIT = 20;

const NamePatterns = () => {
  const [type, setType] = useState<NamePatternDtoTypeEnum>(NamePatternDtoTypeEnum.Party);
  const [overrideName, setOverrideName] = useState('');
  const [baseName, setBaseName] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useNamePatterns(type, {
    overrideName: overrideName || undefined,
    baseName: baseName || undefined,
    page,
    limit: LIMIT,
  });

  const totalPages = Math.max(1, Math.ceil((data?.total || 0) / LIMIT));

  const handleTypeChange = (value: string) => {
    setType(value as NamePatternDtoTypeEnum);
    setPage(1);
  };

  const handleOverrideNameChange = (value: string) => {
    setOverrideName(value);
    setPage(1);
  };

  const handleBaseNameChange = (value: string) => {
    setBaseName(value);
    setPage(1);
  };

  return (
    <AppContent title="Name Patterns">
      <Card>
        <CardHeader>
          <div>
            <CardTitle>Name Patterns</CardTitle>
            <CardDescription>
              Normalized names collected from receipts and bank imports. Remove a base name to stop
              it being auto-normalized to its override in the future — this never changes already
              created receipts, transfers, or records.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <Tabs value={type} onValueChange={handleTypeChange}>
            <TabsList>
              <TabsTrigger value={NamePatternDtoTypeEnum.Party}>Party</TabsTrigger>
              <TabsTrigger value={NamePatternDtoTypeEnum.Item}>Item</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">Override name</label>
              <Input
                className="w-48"
                placeholder="Search override name"
                value={overrideName}
                onChange={(event) => handleOverrideNameChange(event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">Base name</label>
              <Input
                className="w-48"
                placeholder="Search base name"
                value={baseName}
                onChange={(event) => handleBaseNameChange(event.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>
      <div className="flex flex-col gap-2">
        {isLoading && (
          <div className="text-center py-4 text-xs text-muted-foreground">Loading...</div>
        )}
        {data?.data.map((group) => (
          <NamePatternGroupRow key={group.overrideName} type={type} group={group} />
        ))}
        {!isLoading && !data?.data.length && (
          <div className="text-center py-8">
            <Tags className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <CardTitle className="text-lg mb-2">No name patterns found</CardTitle>
            <CardDescription>Adjust the filters above or switch tabs.</CardDescription>
          </div>
        )}
        {!!data?.data.length && (
          <div className="flex items-center justify-between text-xs text-muted-foreground pt-2">
            <span>
              Page {page} of {totalPages} ({data.total} override{data.total === 1 ? '' : 's'})
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="icon"
                disabled={page <= 1}
                onClick={() => setPage((current) => current - 1)}
              >
                <ChevronLeft className="size-3.5" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                disabled={page >= totalPages}
                onClick={() => setPage((current) => current + 1)}
              >
                <ChevronRight className="size-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </AppContent>
  );
};

export default NamePatterns;
